import { SignalQuery } from '@awork/core/state/signal-store/signalQuery'
import type { OnDestroy, Signal } from '@angular/core'
import { computed, Injectable, inject } from '@angular/core'
import { AccountStore } from '@awork/_shared/state/account.store'
import type { AccountState } from '@awork/_shared/models/account.model'
import { Account } from '@awork/_shared/models/account.model'

@Injectable({ providedIn: 'root' })
export class AccountQuery extends SignalQuery<AccountState> implements OnDestroy {
  protected store: AccountStore

  static instance: AccountQuery // Used to query the store in models (user, company, etc.)

  constructor() {
    const store = inject(AccountStore)

    super(store)
    this.store = store

    AccountQuery.instance = this
  }

  /**
   * Clears the static reference when Angular destroys this query.
   */
  ngOnDestroy(): void {
    if (AccountQuery.instance === this) {
      Reflect.deleteProperty(AccountQuery, 'instance')
    }
  }

  /**
   * Gets the account state
   * @returns {AccountState}
   */
  getAccount(): AccountState {
    const state = this.getValue()

    return {
      ...state,
      refreshAt: new Date(state.refreshAt)
    }
  }

  /**
   * Gets the account state
   * @returns {Signal<AccountState>}
   */
  queryAccountState(): Signal<AccountState> {
    return computed(() => {
      const accountState = this.query()

      return {
        ...accountState(),
        refreshAt: new Date(accountState().refreshAt)
      }
    })
  }

  /**
   * Gets the user account
   * @returns {Signal<Account>}
   */
  queryAccount(): Signal<Account> {
    return computed(() => {
      const accountState = this.queryAccountState()
      return Account.fromState(accountState())
    })
  }

  queryTimezone(): Signal<string> {
    return computed(() => {
      const accountState = this.query()
      return accountState().timezone || 'Europe/Berlin'
    })
  }
}
